import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  BarChart, Bar, LineChart, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, Cell, ResponsiveContainer, PieChart, Pie, Line,
  ScatterChart, Scatter, ZAxis, ComposedChart, Area
} from 'recharts';
import { 
  DollarSign, Calendar, AlertCircle, Loader2, PieChart as PieChartIcon, 
  BarChart as BarChartIcon, LineChart as LineChartIcon, Filter, Download, User,
  RefreshCw, TrendingUp, TrendingDown, Briefcase, BookOpen, Search, 
  Calculator, Clock, Award, UserCircle, Zap, BarChart2, Users, Building
} from 'lucide-react';
import { Card, CardHeader, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '')}/api`;

// Color constants for consistent visualization
const COLORS = ['#FE3F06', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#d084d0'];
const STATUS_COLORS = {
  expired: '#ff4d4f',
  warning: '#faad14',
  normal: '#52c41a',
  info: '#FE3F06'
};

const AnalysisPanel = () => {
  // Main state variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('month');
  const [filterCenter, setFilterCenter] = useState('');
  const [filterQualification, setFilterQualification] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Data state variables
  const [trainings, setTrainings] = useState([]);
  const [trainingCenters, setTrainingCenters] = useState([]);
  const [qualificationTypes, setQualificationTypes] = useState([]);
  const [userTrainings, setUserTrainings] = useState([]);
  const [users, setUsers] = useState([]);
  const [userQualifications, setUserQualifications] = useState([]);
  
  // Analytics state
  const [analytics, setAnalytics] = useState({
    totalSpent: 0,
    averageTrainingCost: 0,
    trainingCount: 0,
    participantCount: 0,
    mostExpensiveTraining: null,
    mostCommonQualification: null,
    centerWithMostTrainings: null,
    monthlyTrend: [],
    centerComparison: [],
    qualificationComparison: [],
    maxQualificationCount: 0,
    maxCenterCount: 0
  });


  // Previous period comparison
  const [prevPeriodComparison, setPrevPeriodComparison] = useState({
    totalSpent: 0,
    averageTrainingCost: 0,
    trainingCount: 0
  });

  // Fetch all necessary data on component mount
  useEffect(() => {
    fetchAllData();
  }, []);
  
  // Process analytics whenever filters or date range changes
  useEffect(() => {
    if (trainings.length > 0 && userTrainings.length > 0) {
      const enrichedTrainings = enrichTrainingData(trainings, userTrainings);
      processAnalytics(enrichedTrainings, dateRange, filterCenter, filterQualification, searchTerm);
      
    }
  }, [trainings, userTrainings, userQualifications, dateRange, filterCenter, filterQualification, searchTerm]);

  // Main data fetching function
  const fetchAllData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [
        trainingsResponse, 
        centersResponse, 
        qualificationsResponse,
        actualTrainingsResponse,
        usersResponse,
        userQualificationsResponse
      ] = await Promise.all([
        axios.get(`${API_BASE_URL}/training-center-qualifications/`, config),
        axios.get(`${API_BASE_URL}/training-centers/`, config),
        axios.get(`${API_BASE_URL}/qualificationtype/`, config),
        axios.get(`${API_BASE_URL}/trainings/`, config),
        axios.get(`${API_BASE_URL}/users/`, config),
        axios.get(`${API_BASE_URL}/userqualification/`, config)
      ]);
      
      setTrainings(trainingsResponse.data || []);
      setTrainingCenters(centersResponse.data || []);
      setQualificationTypes(qualificationsResponse.data || []);
      setUserTrainings(actualTrainingsResponse.data || []);
      setUsers(usersResponse.data || []);
      setUserQualifications(userQualificationsResponse.data || []);
      
      setLoading(false);
    } catch (err) {
      console.error('Failed to load analysis data:', err);
      setError('Failed to load analysis data: ' + (err.response?.data?.detail || err.message));
      setLoading(false);
    }
  };

  // Helper function to get date range limits
  const getDateRangeLimits = (range) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    let startDate, endDate;
    
    switch(range) {
      case 'month':
        startDate = new Date(currentYear, currentMonth, 1);
        endDate = new Date(currentYear, currentMonth + 1, 0);
        break;
      case 'quarter':
        const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
        startDate = new Date(currentYear, quarterStartMonth, 1);
        endDate = new Date(currentYear, quarterStartMonth + 3, 0);
        break;
      case 'year':
        startDate = new Date(currentYear, 0, 1);
        endDate = new Date(currentYear, 11, 31);
        break;
      default:
        startDate = new Date(currentYear, currentMonth, 1);
        endDate = new Date(currentYear, currentMonth + 1, 0);
    }
    
    return { startDate, endDate };
  };

  // Enrich training data with user assignments
  const enrichTrainingData = (trainingCenterQualifications, actualTrainings) => {
    const trainingsMap = new Map();
    
    trainingCenterQualifications.forEach(training => {
      trainingsMap.set(training.id, {
        ...training,
        usageCount: 0,
        dates: [],
        totalRevenue: 0,
        userAssignments: []
      });
    });
    
    actualTrainings.forEach(actualTraining => {
      let trainingId = null;
      
      if (typeof actualTraining.training_center_qualification === 'object' && actualTraining.training_center_qualification !== null) {
        trainingId = actualTraining.training_center_qualification.id;
      } else if (typeof actualTraining.training_center_qualification === 'number') {
        trainingId = actualTraining.training_center_qualification;
      } else if (typeof actualTraining.training_center_qualification_id === 'number') {
        trainingId = actualTraining.training_center_qualification_id;
      }
      
      if (trainingId && trainingsMap.has(trainingId)) {
        const training = trainingsMap.get(trainingId);
        
        const participantCount = actualTraining.users_data ? actualTraining.users_data.length : 
                                (actualTraining.users ? actualTraining.users.length : 0);
        
        training.usageCount += participantCount;
        training.totalRevenue += parseFloat(training.price || 0) * participantCount;
        
        if (actualTraining.start_date) {
          training.dates.push(new Date(actualTraining.start_date));
        }
        
        const trainingAssignment = {
          ...actualTraining,
          participants: actualTraining.users_data || actualTraining.users || []
        };
        training.userAssignments.push(trainingAssignment);
        
        trainingsMap.set(trainingId, training);
      }
    });
    
    return Array.from(trainingsMap.values());
  };

  // Calculate percentage change
  const calculatePercentageChange = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  // Process analytics based on actual data with advanced calculations
  const processAnalytics = (enrichedTrainings = [], dateRange, filterCenter, filterQualification, searchTerm) => {
    const { startDate, endDate } = getDateRangeLimits(dateRange);
    const { startDate: prevStartDate, endDate: prevEndDate } = getPreviousPeriodLimits(dateRange);
    
    // Safety check for empty data
    if (!enrichedTrainings.length) {
      setAnalytics({
        totalSpent: 0,
        averageTrainingCost: 0,
        trainingCount: 0,
        mostExpensiveTraining: null,
        mostCommonQualification: null,
        centerWithMostTrainings: null,
        monthlyTrend: [],
        centerComparison: [],
        qualificationComparison: [],
        maxQualificationCount: 0,
        maxCenterCount: 0
      });
      return;
    }
    
    // Filter trainings by date range
    const filteredTrainings = enrichedTrainings.filter(training => {
      return training.dates && training.dates.some(date => 
        date >= startDate && date <= endDate
      );
    });
    
    // Previous period data
    const prevPeriodTrainings = enrichedTrainings.filter(training => {
      return training.dates && training.dates.some(date => 
        date >= prevStartDate && date <= prevEndDate
      );
    });

    // Apply search and filters
    let processedTrainings = filteredTrainings;
    
    if (filterCenter) {
      processedTrainings = processedTrainings.filter(t => 
        t.training_center === parseInt(filterCenter)
      );
    }
    
    if (filterQualification) {
      processedTrainings = processedTrainings.filter(t => 
        t.qualification_type === parseInt(filterQualification)
      );
    }

    if (searchTerm && searchTerm.length > 0) {
      const searchLower = searchTerm.toLowerCase();
      processedTrainings = processedTrainings.filter(t => {
        const centerName = (trainingCenters || []).find(c => c.id === t.training_center)?.name || '';
        const qualName = (qualificationTypes || []).find(q => q.id === t.qualification_type)?.name || '';
        return centerName.toLowerCase().includes(searchLower) || 
               qualName.toLowerCase().includes(searchLower);
      });
    }

    // Previous period calculations
    const prevTotalSpent = prevPeriodTrainings.reduce((sum, t) => 
      sum + (parseFloat(t.price || 0) * t.usageCount), 0);
    const prevTotalUsage = prevPeriodTrainings.reduce((sum, t) => sum + t.usageCount, 0);
    const prevAvgCost = prevTotalUsage > 0 ? prevTotalSpent / prevTotalUsage : 0;
    
    setPrevPeriodComparison({
      totalSpent: prevTotalSpent,
      averageTrainingCost: prevAvgCost,
      trainingCount: prevPeriodTrainings.length
    });

    // Current period calculations
    const totalSpent = processedTrainings.reduce((sum, item) => 
      sum + (parseFloat(item.price || 0) * item.usageCount), 0);
    
    const totalUsage = processedTrainings.reduce((sum, item) => sum + item.usageCount, 0);
    const averageTrainingCost = totalUsage > 0 ? totalSpent / totalUsage : 0;
    
    const mostExpensiveTraining = processedTrainings.reduce((prev, current) => 
      parseFloat(prev.price || 0) > parseFloat(current.price || 0) ? prev : current,
      { price: 0 }
    );

    // Monthly trend analysis
    const monthlyTrend = generateMonthlyTrend(processedTrainings, dateRange);
    
    // Completely rewritten center analysis - simple and reliable
    const centerMap = new Map();
    
    // First, ensure all centers are in the map
    (trainingCenters || []).forEach(center => {
      centerMap.set(center.id, {
        id: center.id,
        name: center.name || `Center ${center.id}`,
        trainings: [],
        totalCost: 0,
        participantCount: 0
      });
    });

    // Then add training data
    processedTrainings.forEach((training, idx) => {
      const centerId = training.training_center_id || 
                      training.training_center || 
                      (training.training_center_data && training.training_center_data.id);
      
      if (centerId && centerMap.has(centerId)) {
        const centerData = centerMap.get(centerId);
        centerData.trainings.push(training);
        centerData.totalCost += parseFloat(training.price || 0) * (training.usageCount || 0);
        centerData.participantCount += training.usageCount || 0;
      }
    });

    // Convert to array with guaranteed unique IDs
    const centerComparison = Array.from(centerMap.values()).map((center, index) => ({
      id: `center_${center.id}_${index}`, // Guaranteed unique
      originalId: center.id,
      name: center.name,
      value: center.totalCost,
      count: center.participantCount,
      trainings: center.trainings.length
    }));

    // Completely rewritten qualification analysis - simple and reliable
    const qualificationMap = new Map();
    
    // First, ensure all qualifications are in the map
    (qualificationTypes || []).forEach(qualification => {
      qualificationMap.set(qualification.id, {
        id: qualification.id,
        name: qualification.name || `Qualification ${qualification.id}`,
        trainings: [],
        totalCost: 0,
        participantCount: 0
      });
    });

    // Then add training data
    processedTrainings.forEach((training, idx) => {
      const qualificationId = training.qualification_type_id || 
                             training.qualification_type || 
                             (training.qualification_data && training.qualification_data.id);
      
      if (qualificationId && qualificationMap.has(qualificationId)) {
        const qualificationData = qualificationMap.get(qualificationId);
        qualificationData.trainings.push(training);
        qualificationData.totalCost += parseFloat(training.price || 0) * (training.usageCount || 0);
        qualificationData.participantCount += training.usageCount || 0;
      }
    });

    // Convert to array with guaranteed unique IDs
    const qualificationComparison = Array.from(qualificationMap.values()).map((qualification, index) => ({
      id: `qualification_${qualification.id}_${index}`, // Guaranteed unique
      originalId: qualification.id,
      name: qualification.name,
      value: qualification.totalCost,
      count: qualification.participantCount,
      trainings: qualification.trainings.length
    }));

    // User statistics for Employee Analysis tab
    const userStats = (users || []).map(user => {
      const userTrainingAssignments = processedTrainings.filter(training => {
        return training.userAssignments.some(assignment => 
          assignment.users_data && assignment.users_data.some(u => u.id === user.id)
        );
      });
      
      const userQualificationsSet = new Set();
      const totalTrainingCost = userTrainingAssignments.reduce((sum, training) => {
        const userInAssignments = training.userAssignments.filter(assignment =>
          assignment.users_data && assignment.users_data.some(u => u.id === user.id)
        ).length;
        
        // Add qualifications to set
        if (training.qualification_data) {
          userQualificationsSet.add(training.qualification_data.name);
        }
        
        return sum + (parseFloat(training.price || 0) * userInAssignments);
      }, 0);

      return {
        id: user.id,
        name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username,
        username: user.username,
        trainingCount: userTrainingAssignments.length,
        totalCost: totalTrainingCost,
        qualifications: Array.from(userQualificationsSet).map((name, index) => ({
          id: index,
          name
        }))
      };
    }).filter(user => user.trainingCount > 0); // Only users with trainings

    // Calculate additional metrics
    const participantCount = userStats.length;
    const averageCostPerParticipant = participantCount > 0 ? totalSpent / participantCount : 0;

    // Simple, clean analytics object
    const analyticsData = {
      totalSpent: totalSpent || 0,
      averageTrainingCost: averageTrainingCost || 0,
      trainingCount: processedTrainings.length,
      participantCount: participantCount || 0,
      averageCostPerParticipant: averageCostPerParticipant || 0,
      mostExpensiveTraining: mostExpensiveTraining.price > 0 ? mostExpensiveTraining : null,
      mostCommonQualification: qualificationComparison.length > 0 ? 
        qualificationComparison.reduce((prev, current) => (prev.count || 0) > (current.count || 0) ? prev : current) : null,
      centerWithMostTrainings: centerComparison.length > 0 ?
        centerComparison.reduce((prev, current) => (prev.trainings || 0) > (current.trainings || 0) ? prev : current) : null,
      monthlyTrend: monthlyTrend || [],
      centerComparison: centerComparison || [],
      qualificationComparison: qualificationComparison || [],
      qualificationStats: qualificationComparison.map((q, idx) => ({
        id: `qual_stat_${q.originalId || idx}`,
        originalId: q.originalId,
        name: q.name || 'Unknown',
        trainingCount: q.trainings || 0,
        participantCount: q.count || 0,
        totalCost: q.value || 0,
        avgPrice: (q.count && q.count > 0) ? (q.value || 0) / q.count : 0
      })),
      centerStats: centerComparison.map((c, idx) => ({
        id: `center_stat_${c.originalId || idx}`,
        originalId: c.originalId,
        name: c.name || 'Unknown',
        trainingCount: c.trainings || 0,
        participantCount: c.count || 0,
        totalCost: c.value || 0
      })),
      userStats: userStats || [],
      maxQualificationCount: qualificationComparison.length > 0 ? 
        Math.max(...qualificationComparison.map(q => q.count || 0)) : 0,
      maxCenterCount: centerComparison.length > 0 ?
        Math.max(...centerComparison.map(c => c.count || 0)) : 0
    };

    console.log('📊 New Analytics Data:', {
      centerComparison: analyticsData.centerComparison,
      qualificationComparison: analyticsData.qualificationComparison
    });
    
    setAnalytics(analyticsData);
  };

  // Generate monthly trend data based on date range
  const generateMonthlyTrend = (trainings, range) => {
    if (range === 'month') {
      // Daily data for current month
      const trendData = {};
      for (let i = 1; i <= 31; i++) {
        trendData[i] = {
          day: i,
          trainings: 0,
          cost: 0,
          participants: 0
        };
      }

      trainings.forEach(training => {
        (training.dates || []).forEach(date => {
          const day = date.getDate();
          if (trendData[day]) {
            trendData[day].trainings += 1;
            trendData[day].cost += parseFloat(training.price || 0);
            trendData[day].participants += (training.usageCount || 0);
          }
        });
      });

      return Object.values(trendData).filter(d => d.trainings > 0);
    } 
    
    if (range === 'quarter') {
      // Weekly data for current quarter
      const trendData = Array.from({length: 13}, (_, index) => ({
        week: index + 1,
        trainings: 0,
        cost: 0,
        participants: 0
      }));

      trainings.forEach(training => {
        (training.dates || []).forEach(date => {
          const weekOfYear = Math.ceil((date - new Date(date.getFullYear(), 0, 1)) / (7 * 24 * 60 * 60 * 1000));
          const quarterWeek = weekOfYear % 13;
          if (quarterWeek < trendData.length) {
            trendData[quarterWeek].trainings += 1;
            trendData[quarterWeek].cost += parseFloat(training.price || 0);
            trendData[quarterWeek].participants += (training.usageCount || 0);
          }
        });
      });

      return trendData.filter(d => d.trainings > 0);
    }
    
    if (range === 'year') {
      // Monthly data for current year
      const trendData = Array.from({length: 12}, (_, index) => ({
        month: new Date(0, index).toLocaleString('default', { month: 'short' }),
        trainings: 0,
        cost: 0,
        participants: 0
      }));

      trainings.forEach(training => {
        (training.dates || []).forEach(date => {
          const month = date.getMonth();
          if (month < trendData.length) {
            trendData[month].trainings += 1;
            trendData[month].cost += parseFloat(training.price || 0);
            trendData[month].participants += (training.usageCount || 0);
          }
        });
      });

      return trendData.filter(d => d.trainings > 0);
    }

    return [];
  };

  // Get previous period limits for comparison
  const getPreviousPeriodLimits = (range) => {
    const { startDate, endDate } = getDateRangeLimits(range);
    let prevStartDate, prevEndDate;
    
    switch(range) {
      case 'month':
        prevStartDate = new Date(startDate);
        prevStartDate.setMonth(prevStartDate.getMonth() - 1);
        prevEndDate = new Date(startDate);
        prevEndDate.setDate(prevEndDate.getDate() - 1);
        break;
      case 'quarter':
        prevStartDate = new Date(startDate);
        prevStartDate.setMonth(prevStartDate.getMonth() - 3);
        prevEndDate = new Date(startDate);
        prevEndDate.setDate(prevEndDate.getDate() - 1);
        break;
      case 'year':
        prevStartDate = new Date(startDate);
        prevStartDate.setFullYear(prevStartDate.getFullYear() - 1);
        prevEndDate = new Date(endDate);
        prevEndDate.setFullYear(prevEndDate.getFullYear() - 1);
        break;
      default:
        prevStartDate = new Date(startDate);
        prevStartDate.setMonth(prevStartDate.getMonth() - 1);
        prevEndDate = new Date(startDate);
        prevEndDate.setDate(prevEndDate.getDate() - 1);
    }
    
    return { startDate: prevStartDate, endDate: prevEndDate };
  };


  // Search change handler with debouncing
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Export data functionality
  const handleExportData = () => {
    const dataStr = JSON.stringify(analytics, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `training-analysis-${dateRange}-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-xl shadow-lg border border-steel-200">
          <p className="font-semibold text-slate-800">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.name.includes('Cost') || entry.name.includes('Spent') ? 
                `${entry.value.toFixed(2)} zł` : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-white rounded-xl shadow-lg">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-brand-500 mx-auto mb-4" />
          <p className="text-slate-600">Loading analysis data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-white rounded-xl shadow-lg">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-slate-800 font-semibold mb-2">Error loading data</p>
          <p className="text-slate-600 text-sm">{error}</p>
          <Button onClick={fetchAllData} className="mt-4 bg-brand-500 text-white">
            <RefreshCw className="h-4 w-4 mr-2" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg h-full overflow-auto border border-steel-100">
      {/* Header section */}
      <div className="bg-gradient-to-r from-brand-50 to-steel-50 px-6 py-5 flex justify-between items-center border-b border-steel-200">
        <h2 className="text-2xl font-bold text-slate-900 flex items-center">
          Training Analysis Dashboard
          <span className="ml-3 bg-brand-500 text-white text-xs font-bold px-2 py-1 rounded-full">
            {analytics.trainingCount} trainings
          </span>
        </h2>
        <div className="flex gap-2">
          <Button
            onClick={() => setDateRange('month')}
            className={`${dateRange === 'month' ? 'bg-brand-500 text-white' : 'bg-white text-slate-700 border border-steel-300'} px-3 py-1.5 text-sm rounded-xl shadow-sm`}
          >
            Monthly
          </Button>
          <Button
            onClick={() => setDateRange('quarter')}
            className={`${dateRange === 'quarter' ? 'bg-brand-500 text-white' : 'bg-white text-slate-700 border border-steel-300'} px-3 py-1.5 text-sm rounded-xl shadow-sm`}
          >
            Quarterly
          </Button>
          <Button
            onClick={() => setDateRange('year')}
            className={`${dateRange === 'year' ? 'bg-brand-500 text-white' : 'bg-white text-slate-700 border border-steel-300'} px-3 py-1.5 text-sm rounded-xl shadow-sm`}
          >
            Yearly
          </Button>
        </div>
      </div>
      
      {/* Filter section */}
      <div className="px-6 pt-4 pb-2 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search trainings..."
            className="block w-full border border-steel-300 rounded-xl pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
          />
        </div>
        <div className="flex gap-4">
          <select
            value={filterCenter}
            onChange={(e) => setFilterCenter(e.target.value)}
            className="border border-steel-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
          >
            <option value="">All centers</option>
            {trainingCenters.map((center) => (
              <option key={center.id} value={center.id}>
                {center.name}
              </option>
            ))}
          </select>
          <select
            value={filterQualification}
            onChange={(e) => setFilterQualification(e.target.value)}
            className="border border-steel-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
          >
            <option value="">All qualifications</option>
            {qualificationTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
          <Button
            className="bg-brand-500 text-white px-3 py-2 rounded-xl hover:bg-brand-600 transition-colors shadow-lg shadow-brand-500/20"
            onClick={handleExportData}
          >
            <Download className="h-4 w-4 mr-1" /> Export
          </Button>
        </div>
      </div>
      
      {/* Tab navigation */}
      <div className="border-b border-steel-200 px-6">
        <nav className="-mb-px flex overflow-x-auto">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-4 px-6 font-medium text-sm whitespace-nowrap focus:outline-none ${
              activeTab === 'overview'
                ? 'border-b-2 border-brand-500 text-brand-500'
                : 'text-slate-500 hover:text-slate-700 hover:border-steel-300 border-transparent'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('byCenter')}
            className={`py-4 px-6 font-medium text-sm whitespace-nowrap focus:outline-none ${
              activeTab === 'byCenter'
                ? 'border-b-2 border-brand-500 text-brand-500'
                : 'text-slate-500 hover:text-slate-700 hover:border-steel-300 border-transparent'
            }`}
          >
            By Center
          </button>
          <button
            onClick={() => setActiveTab('byQualification')}
            className={`py-4 px-6 font-medium text-sm whitespace-nowrap focus:outline-none ${
              activeTab === 'byQualification'
                ? 'border-b-2 border-brand-500 text-brand-500'
                : 'text-slate-500 hover:text-slate-700 hover:border-steel-300 border-transparent'
            }`}
          >
            By Qualification
          </button>
          <button
            onClick={() => setActiveTab('trends')}
            className={`py-4 px-6 font-medium text-sm whitespace-nowrap focus:outline-none ${
              activeTab === 'trends'
                ? 'border-b-2 border-brand-500 text-brand-500'
                : 'text-slate-500 hover:text-slate-700 hover:border-steel-300 border-transparent'
            }`}
          >
            Trends
          </button>
          <button
            onClick={() => setActiveTab('employees')}
            className={`py-4 px-6 font-medium text-sm whitespace-nowrap focus:outline-none ${
              activeTab === 'employees'
                ? 'border-b-2 border-brand-500 text-brand-500'
                : 'text-slate-500 hover:text-slate-700 hover:border-steel-300 border-transparent'
            }`}
          >
            <UserCircle className="h-4 w-4 inline mr-1" />
            Employee Analysis
          </button>
        </nav>
      </div>
      
      {/* Tab content */}
      <div className="p-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Key metrics cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-slate-500 text-sm font-medium">Total Spent</h3>
                    <div className="p-2 bg-brand-100 rounded-full text-brand-500">
                      <DollarSign className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-2xl font-bold text-slate-900">{analytics.totalSpent.toFixed(2)} zł</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {dateRange === 'month' ? 'This month' : dateRange === 'quarter' ? 'This quarter' : 'This year'}
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-slate-500 text-sm font-medium">Avg Cost/Person</h3>
                    <div className="p-2 bg-green-100 rounded-full text-green-600">
                      <Calculator className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-2xl font-bold text-slate-900">{analytics.averageTrainingCost.toFixed(2)} zł</p>
                    <p className="mt-1 text-xs text-slate-500">Per participant</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-slate-500 text-sm font-medium">Total Trainings</h3>
                    <div className="p-2 bg-brand-100 rounded-full text-brand-500">
                      <BookOpen className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-2xl font-bold text-slate-900">{analytics.trainingCount}</p>
                    <p className="mt-1 text-xs text-slate-500">Completed trainings</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-slate-500 text-sm font-medium">Participants</h3>
                    <div className="p-2 bg-brand-100 rounded-full text-brand-500">
                      <Users className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-2xl font-bold text-slate-900">{analytics.participantCount || 0}</p>
                    <p className="mt-1 text-xs text-slate-500">Total trained</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Monthly Trend Chart */}
            {analytics.monthlyTrend.length > 0 && (
              <Card className="shadow-sm">
                <CardHeader>
                  <h3 className="text-lg font-semibold text-slate-800">Monthly Trend</h3>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={analytics.monthlyTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" orientation="left" stroke="#FE3F06" />
                      <YAxis yAxisId="right" orientation="right" stroke="#10b981" />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="cost" fill="#FE3F06" name="Total Cost (zł)" />
                      <Line yAxisId="right" type="monotone" dataKey="participants" stroke="#10b981" name="Participants" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Quick insights */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {analytics.mostExpensiveTraining && (
                <Card className="shadow-sm">
                  <CardContent className="p-6">
                    <h4 className="text-sm font-medium text-slate-500 mb-2">Most Expensive Training</h4>
                    <p className="text-lg font-semibold text-slate-900">
                      {analytics.mostExpensiveTraining.price} zł
                    </p>
                    <p className="text-sm text-slate-600 mt-1">
                      {qualificationTypes.find(q => q.id === analytics.mostExpensiveTraining.qualification_type)?.name || 'Unknown'}
                    </p>
                  </CardContent>
                </Card>
              )}
              
              {analytics.mostPopularQualification && (
                <Card className="shadow-sm">
                  <CardContent className="p-6">
                    <h4 className="text-sm font-medium text-slate-500 mb-2">Most Popular Qualification</h4>
                    <p className="text-lg font-semibold text-slate-900">
                      {analytics.mostPopularQualification.name}
                    </p>
                    <p className="text-sm text-slate-600 mt-1">
                      {analytics.mostPopularQualification.participantCount} participants
                    </p>
                  </CardContent>
                </Card>
              )}
              
              {analytics.centerWithMostTrainings && (
                <Card className="shadow-sm">
                  <CardContent className="p-6">
                    <h4 className="text-sm font-medium text-slate-500 mb-2">Most Active Center</h4>
                    <p className="text-lg font-semibold text-slate-900">
                      {analytics.centerWithMostTrainings.name}
                    </p>
                    <p className="text-sm text-slate-600 mt-1">
                      {analytics.centerWithMostTrainings.trainingCount} trainings
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* By Center Tab */}
        {activeTab === 'byCenter' && (
          <div className="space-y-6">
            <Card className="shadow-sm">
              <CardHeader>
                <h3 className="text-lg font-semibold text-slate-800">Analysis By Training Center</h3>
              </CardHeader>
              <CardContent>
                {(analytics.centerComparison || []).length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart 
                        key={`centers-${Date.now()}`}
                        data={analytics.centerComparison || []}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis 
                          dataKey="name" 
                          angle={-45} 
                          textAnchor="end" 
                          height={100}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar dataKey="value" fill="#FE3F06" name="Total Cost (zł)" />
                        <Bar dataKey="count" fill="#10b981" name="Participants" />
                        <Bar dataKey="trainings" fill="#f59e0b" name="Trainings" />
                      </BarChart>
                    </ResponsiveContainer>
                    
                    <div className="mt-6 overflow-x-auto">
                      <table className="min-w-full divide-y divide-steel-200">
                        <thead className="bg-steel-100 border-b-2 border-brand-500">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                              Center Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                              Trainings
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                              Participants
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                              Total Cost
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                              Avg Cost/Person
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-steel-200">
                          {(analytics.centerComparison || []).map((center) => (
                            <tr key={center.id} className="hover:bg-brand-50 transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                                {center.name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                                {center.trainings}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                                {center.count}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                                {center.value.toFixed(2)} zł
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                                {center.count > 0 ? 
                                  (center.value / center.count).toFixed(2) : '0.00'} zł
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <Building className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-500">No training center data available for the selected period</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* By Qualification Tab */}
        {activeTab === 'byQualification' && (
          <div className="space-y-6">
            <Card className="shadow-sm">
              <CardHeader>
                <h3 className="text-lg font-semibold text-slate-800">Analysis By Qualification Type</h3>
              </CardHeader>
              <CardContent>
                {(analytics.qualificationStats || []).length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie
                          data={analytics.qualificationStats}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={120}
                          fill="#FE3F06"
                          dataKey="participantCount"
                        >
                          {analytics.qualificationStats.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {analytics.qualificationStats.map((qual, index) => (
                        <Card key={qual.id} className="shadow-sm">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold text-slate-900">{qual.name}</h4>
                              <div 
                                className="w-4 h-4 rounded" 
                                style={{ backgroundColor: COLORS[index % COLORS.length] }}
                              />
                            </div>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Trainings:</span>
                                <span className="font-medium">{qual.trainingCount}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Participants:</span>
                                <span className="font-medium">{qual.participantCount}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Total Cost:</span>
                                <span className="font-medium">{qual.totalCost.toFixed(2)} zł</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Avg Price:</span>
                                <span className="font-medium">{qual.avgPrice.toFixed(2)} zł</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <Award className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-500">No qualification data available for the selected period</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Trends Tab */}
        {activeTab === 'trends' && (
          <div className="space-y-6">
            <Card className="shadow-sm">
              <CardHeader>
                <h3 className="text-lg font-semibold text-slate-800">Training Trends Analysis</h3>
              </CardHeader>
              <CardContent>
                {(analytics.monthlyTrend || []).length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={analytics.monthlyTrend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line type="monotone" dataKey="totalCost" stroke="#FE3F06" name="Total Cost (zł)" strokeWidth={3} />
                        <Line type="monotone" dataKey="participantCount" stroke="#10b981" name="Participants" strokeWidth={3} />
                        <Line type="monotone" dataKey="trainingCount" stroke="#ffc658" name="Trainings" strokeWidth={3} />
                      </LineChart>
                    </ResponsiveContainer>
                    
                    {/* Trend insights */}
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Card className="shadow-sm">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-slate-900">Monthly Growth</h4>
                            <TrendingUp className="h-5 w-5 text-green-600" />
                          </div>
                          <div className="mt-2">
                            <p className="text-2xl font-bold text-green-600">
                              {analytics.monthlyTrend.length > 1 ? (
                                ((analytics.monthlyTrend[analytics.monthlyTrend.length - 1].totalCost - 
                                  analytics.monthlyTrend[analytics.monthlyTrend.length - 2].totalCost) / 
                                  analytics.monthlyTrend[analytics.monthlyTrend.length - 2].totalCost * 100).toFixed(1)
                              ) : 0}%
                            </p>
                            <p className="text-xs text-slate-500">Cost change from last month</p>
                          </div>
                        </CardContent>
                      </Card>
                      
                      <Card className="shadow-sm">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-slate-900">Peak Month</h4>
                            <BarChart2 className="h-5 w-5 text-brand-500" />
                          </div>
                          <div className="mt-2">
                            <p className="text-lg font-bold text-slate-900">
                              {analytics.monthlyTrend.reduce((max, curr) => 
                                curr.totalCost > max.totalCost ? curr : max, 
                                analytics.monthlyTrend[0] || {month: 'N/A', totalCost: 0}
                              ).month}
                            </p>
                            <p className="text-xs text-slate-500">Highest spending month</p>
                          </div>
                        </CardContent>
                      </Card>
                      
                      <Card className="shadow-sm">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-slate-900">Average Monthly</h4>
                            <Calculator className="h-5 w-5 text-brand-500" />
                          </div>
                          <div className="mt-2">
                            <p className="text-lg font-bold text-slate-900">
                              {analytics.monthlyTrend.length > 0 ? (
                                (analytics.monthlyTrend.reduce((sum, month) => sum + month.totalCost, 0) / 
                                analytics.monthlyTrend.length).toFixed(0)
                              ) : 0} zł
                            </p>
                            <p className="text-xs text-slate-500">Average monthly cost</p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <TrendingUp className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-500">No trend data available for the selected period</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Qualifications Tab - Legacy */}
        {activeTab === 'qualifications' && (
          <div className="space-y-6">
            <Card className="shadow-sm">
              <CardHeader>
                <h3 className="text-lg font-semibold text-slate-800">Qualifications Analysis</h3>
              </CardHeader>
              <CardContent>
                {analytics.qualificationStats.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie
                          data={analytics.qualificationStats}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={120}
                          fill="#FE3F06"
                          dataKey="participantCount"
                        >
                          {analytics.qualificationStats.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {analytics.qualificationStats.map((qual, index) => (
                        <Card key={qual.id} className="shadow-sm">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold text-slate-900">{qual.name}</h4>
                              <div 
                                className="w-4 h-4 rounded" 
                                style={{ backgroundColor: COLORS[index % COLORS.length] }}
                              />
                            </div>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Trainings:</span>
                                <span className="font-medium">{qual.trainingCount}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Participants:</span>
                                <span className="font-medium">{qual.participantCount}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Total Cost:</span>
                                <span className="font-medium">{qual.totalCost.toFixed(2)} zł</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Avg Price:</span>
                                <span className="font-medium">{qual.avgPrice.toFixed(2)} zł</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <Award className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-500">No qualification data available for the selected period</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Employees Tab */}
        {activeTab === 'employees' && (
          <div className="space-y-6">
            {/* Employee Training Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-slate-500 text-sm font-medium">Total Employees</h3>
                    <Users className="h-5 w-5 text-brand-500" />
                  </div>
                  <div className="mt-4">
                    <p className="text-2xl font-bold text-slate-900">
                      {(analytics.userStats || []).length}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">With trainings</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-slate-500 text-sm font-medium">Avg Cost/Employee</h3>
                    <Calculator className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="mt-4">
                    <p className="text-2xl font-bold text-slate-900">
                      {analytics.userStats && analytics.userStats.length > 0 
                        ? (analytics.userStats.reduce((sum, u) => sum + u.totalCost, 0) / analytics.userStats.length).toFixed(0)
                        : 0} zł
                    </p>
                    <p className="mt-1 text-xs text-slate-500">Average investment</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-slate-500 text-sm font-medium">Most Trained</h3>
                    <Award className="h-5 w-5 text-brand-500" />
                  </div>
                  <div className="mt-4">
                    <p className="text-lg font-bold text-slate-900 truncate">
                      {analytics.userStats && analytics.userStats.length > 0
                        ? analytics.userStats.reduce((max, u) => u.trainingCount > max.trainingCount ? u : max).name
                        : 'N/A'}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {analytics.userStats && analytics.userStats.length > 0
                        ? `${analytics.userStats.reduce((max, u) => u.trainingCount > max.trainingCount ? u : max).trainingCount} trainings`
                        : 'No data'}
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-slate-500 text-sm font-medium">Total Invested</h3>
                    <DollarSign className="h-5 w-5 text-red-600" />
                  </div>
                  <div className="mt-4">
                    <p className="text-2xl font-bold text-slate-900">
                      {analytics.userStats 
                        ? analytics.userStats.reduce((sum, u) => sum + u.totalCost, 0).toFixed(0)
                        : 0} zł
                    </p>
                    <p className="mt-1 text-xs text-slate-500">In employee training</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Top Performers Chart */}
            <Card className="shadow-sm">
              <CardHeader>
                <h3 className="text-lg font-semibold text-slate-800">Top 10 Most Trained Employees</h3>
                <p className="text-sm text-slate-500 mt-1">Ranking by number of completed trainings</p>
              </CardHeader>
              <CardContent>
                {(analytics.userStats || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart 
                      key={`employees-${Date.now()}`}
                      data={(analytics.userStats || [])
                        .sort((a, b) => (b.trainingCount || 0) - (a.trainingCount || 0))
                        .slice(0, 10)
                        .map((user, index) => ({
                          id: `user_${user.id}_${index}`,
                          name: user.name || 'Unknown User',
                          displayName: `${user.name || 'User'} (${user.username || user.id})`,
                          trainingCount: user.trainingCount || 0,
                          totalCost: user.totalCost || 0,
                          qualifications: user.qualifications || 0
                        }))}
                      layout="horizontal"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis type="number" />
                      <YAxis 
                        dataKey="displayName" 
                        type="category" 
                        width={150}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-white p-3 rounded-xl shadow-lg border border-steel-200">
                                <p className="font-semibold text-slate-800">{data.name || 'Unknown User'}</p>
                                <p className="text-sm text-slate-600">Trainings: {data.trainingCount || 0}</p>
                                <p className="text-sm text-slate-600">Total Cost: {(data.totalCost || 0).toFixed(2)} zł</p>
                                <p className="text-sm text-slate-600">Qualifications: {data.qualifications || 0}</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="trainingCount" fill="#FE3F06" name="Trainings" radius={[0, 4, 4, 0]} />
                      {/* Fix for duplicate keys warning */}
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-12">
                    <UserCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-500">No employee training data available</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Cost Distribution Chart */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="shadow-sm">
                <CardHeader>
                  <h3 className="text-lg font-semibold text-slate-800">Training Cost Distribution</h3>
                  <p className="text-sm text-slate-500 mt-1">Investment per employee</p>
                </CardHeader>
                <CardContent>
                  {(analytics.userStats || []).length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <ScatterChart>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis 
                          dataKey="trainingCount" 
                          name="Trainings" 
                          label={{ value: 'Number of Trainings', position: 'insideBottom', offset: -5 }}
                        />
                        <YAxis 
                          dataKey="totalCost" 
                          name="Cost" 
                          label={{ value: 'Total Cost (zł)', angle: -90, position: 'insideLeft' }}
                        />
                        <Tooltip 
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <div className="bg-white p-3 rounded-xl shadow-lg border border-steel-200">
                                  <p className="font-semibold text-slate-800">{data.name}</p>
                                  <p className="text-sm text-slate-600">Trainings: {data.trainingCount}</p>
                                  <p className="text-sm text-slate-600">Total Cost: {data.totalCost.toFixed(2)} zł</p>
                                  <p className="text-sm text-slate-600">Avg Cost: {(data.totalCost / data.trainingCount).toFixed(2)} zł</p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Scatter
                          data={analytics.userStats}
                          fill="#FE3F06"
                          name="Employees"
                        >
                          {analytics.userStats.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.totalCost > 5000 ? '#ef4444' : 
                                    entry.totalCost > 2500 ? '#f59e0b' : 
                                    '#10b981'} 
                            />
                          ))}
                        </Scatter>
                      </ScatterChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center py-12">
                      <BarChart2 className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-500">No data to display</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="shadow-sm">
                <CardHeader>
                  <h3 className="text-lg font-semibold text-slate-800">Qualification Coverage</h3>
                  <p className="text-sm text-slate-500 mt-1">Employees by qualification count</p>
                </CardHeader>
                <CardContent>
                  {(analytics.userStats || []).length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={(() => {
                            const qualGroups = {};
                            (analytics.userStats || []).forEach(user => {
                              const qualCount = (user.qualifications || []).length;
                              const key = qualCount === 0 ? 'No Qualifications' :
                                         qualCount === 1 ? '1 Qualification' :
                                         qualCount <= 3 ? '2-3 Qualifications' :
                                         qualCount <= 5 ? '4-5 Qualifications' :
                                         '6+ Qualifications';
                              qualGroups[key] = (qualGroups[key] || 0) + 1;
                            });
                            return Object.entries(qualGroups).map(([name, value]) => ({ name, value }));
                          })()}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={100}
                          fill="#FE3F06"
                          dataKey="value"
                        >
                          {[0, 1, 2, 3, 4].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center py-12">
                      <Award className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                      <p className="text-slate-500">No qualification data</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Employee Details Grid */}
            <Card className="shadow-sm">
              <CardHeader>
                <h3 className="text-lg font-semibold text-slate-800">Employee Training Details</h3>
                <p className="text-sm text-slate-500 mt-1">Comprehensive training overview</p>
              </CardHeader>
              <CardContent>
                {(analytics.userStats || []).length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(analytics.userStats || [])
                      .sort((a, b) => b.totalCost - a.totalCost)
                      .slice(0, 12)
                      .map((user) => (
                        <div key={user.id} className="bg-steel-50 rounded-xl p-4 hover:shadow-md hover:bg-brand-50 transition-all">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-semibold text-slate-900">{user.name}</h4>
                              <p className="text-xs text-slate-500">@{user.username}</p>
                            </div>
                            <UserCircle className="h-8 w-8 text-slate-400" />
                          </div>
                          
                          <div className="space-y-2 mt-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-slate-600">Trainings:</span>
                              <span className="text-sm font-semibold text-brand-500">{user.trainingCount}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-slate-600">Total Cost:</span>
                              <span className="text-sm font-semibold text-green-600">{user.totalCost.toFixed(0)} zł</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-slate-600">Qualifications:</span>
                              <span className="text-sm font-semibold text-brand-500">{user.qualifications.length}</span>
                            </div>
                          </div>
                          
                          {user.qualifications.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-steel-200">
                              <div className="flex flex-wrap gap-1">
                                {user.qualifications.slice(0, 3).map((qual, idx) => (
                                  <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand-100 text-brand-700">
                                    {qual.name}
                                  </span>
                                ))}
                                {user.qualifications.length > 3 && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-steel-100 text-slate-600">
                                    +{user.qualifications.length - 3} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-500">No employee training data available for the selected period</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}


      </div>
    </div>
  );
};

export default AnalysisPanel;